// src/pages/ia-paca/aix-en-provence.tsx
//
// Page géo — Aix-en-Provence (campus tech, R&D, ESS, Arbois).
// Stratégie : capter "recrutement IA Aix-en-Provence", "AI Engineer Aix",
// "Data Scientist Aix Marseille Provence", "Arbois", "Pôle Pégase".

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/layout';
import IaPacaHero from '../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../components/ia-paca/IaSalaryTable';
import {
  ArrowLeft,
  ArrowRight,
  Cpu,
  GraduationCap,
  Building2,
  FlaskConical,
  MapPin,
  TreePine,
} from 'lucide-react';

export default function AixEnProvenceIaPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Recruteurs.io — Recrutement IA Aix-en-Provence',
    description:
      "Cabinet de recrutement IA à Aix-en-Provence. Profils AI Engineer, Data Scientist, MLOps pour les ETI, scale-ups et laboratoires de l'agglomération Aix-Marseille-Provence.",
    url: 'https://recruteurs.io/ia-paca/aix-en-provence',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Aix-en-Provence',
      addressRegion: "Provence-Alpes-Côte d'Azur",
      addressCountry: 'FR',
    },
    areaServed: { '@type': 'City', name: 'Aix-en-Provence' },
  };

  return (
    <Layout
      title="Recrutement IA à Aix-en-Provence — AI Engineer, Data Scientist | recruteurs.io"
      description="Cabinet de recrutement IA à Aix-en-Provence. Technopôle Arbois, Pôle Pégase, ETI industrielles, scale-ups, R&D. Cabinet local pour AI Engineer, Data Scientist, MLOps, Architecte IA."
      keywords="recrutement IA Aix-en-Provence, AI Engineer Aix, Data Scientist Aix, recrutement intelligence artificielle Aix Marseille Provence, Arbois, Pôle Pégase, recrutement tech Aix"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Aix-en-Provence', href: '/ia-paca/aix-en-provence' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <IaPacaHero
        eyebrow="Recrutement IA · Aix-en-Provence"
        title="Recruter en IA"
        highlight="à Aix-en-Provence."
        subtitle="Aix concentre la R&D, les ETI industrielles, et un vivier académique solide (AMU, IAE, Centrale Med, Polytech Marseille). Moins visible que Marseille ou Sophia, mais redoutablement efficace pour les recrutements Data Science, IA industrielle et profils confirmés."
        badges={['Technopôle Arbois', 'Pôle Pégase', 'AMU', 'CEA Cadarache', 'ITER']}
      />

      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Toutes les villes IA en PACA
          </Link>
        </div>
      </div>

      {/* Marché Aix */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Marché IA & R&D — Aix-en-Provence 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Aix, hub R&D et industrie pour l'IA en PACA.
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            <p>
              <strong>Aix-en-Provence + son aire urbaine (~400 000 hab.)</strong>{' '}
              combinent un tissu d'ETI industrielles (énergie, aéronautique,
              matériaux), un pôle académique majeur (Aix-Marseille Université,
              80 000 étudiants, premier pôle universitaire francophone) et la
              proximité du <strong>CEA Cadarache + ITER</strong> côté nord.
            </p>
            <p>
              Le <strong>Technopôle de l'Environnement Arbois-Méditerranée</strong>{' '}
              (250+ structures, R&D environnement / énergie / IA appliquée),
              le <strong>Pôle Pégase</strong> (aéronautique drones IA), et la
              <strong> Z.A. Les Milles</strong> (10 000+ entreprises, dont
              beaucoup de PME tech) constituent les 3 zones de chasse
              prioritaires pour les profils IA.
            </p>
            <p>
              Profil dominant à Aix : <strong>Data Scientist confirmé,
              ingénieur IA industrielle, AI/ML pour systèmes complexes</strong>{' '}
              (énergie, défense, environnement). Moins de pure-play LLM
              qu'à Marseille, mais des cas d'usage métier matures.
            </p>
          </div>
        </div>
      </section>

      {/* Écosystème Aix */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Écosystème Aix
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Arbois, Pôle Pégase, AMU — l'infrastructure recherche & industrie.
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
            {ECOSYSTEME_AIX.map((e) => (
              <div
                key={e.nom}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-center gap-2">
                  <e.Icon className="h-5 w-5 text-violet-600" />
                  <h3 className="text-base font-bold text-slate-900">{e.nom}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-600">{e.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profils types Aix */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Qui recrute en IA à Aix ?
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            3 typologies d'employeurs IA à Aix en 2026.
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            <TypologieCard
              Icon={Building2}
              tag="ETI industrielles"
              title="Énergie, aéronautique, matériaux"
              body="ETI implantées historiquement à Aix ou autour (Z.A. Les Milles, Arbois, Rousset). Cas d'usage IA : maintenance prédictive, computer vision contrôle qualité, optimisation procédés, jumeau numérique. Profils Data Science + ingé IA industrielle."
            />
            <TypologieCard
              Icon={FlaskConical}
              tag="R&D et laboratoires"
              title="CEA, INRAE, AMU, ITER"
              body="L'environnement R&D autour d'Aix (CEA Cadarache, ITER, INRAE, labos AMU) génère un flux constant de projets IA appliqués à l'énergie, l'environnement, la physique. Profils PhD ou ingés R&D, exigence scientifique élevée."
            />
            <TypologieCard
              Icon={Cpu}
              tag="PME tech & scale-ups"
              title="Software, SaaS B2B, GreenTech"
              body="Aix attire des PME tech qui ont besoin de profils tech à coût plus contenu que Sophia, sans la complexité urbaine de Marseille. SaaS B2B, GreenTech, drones, MedTech. Profils confirmés AI Engineer / Data Scientist."
            />
          </div>
        </div>
      </section>

      {/* Salaires Aix */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires IA Aix — mars 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Aix vs Marseille : -2 à -5%, mais marché moins liquide.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Les packages IA à Aix sont légèrement inférieurs à Marseille (-2
            à -5%), mais la disponibilité des profils est plus faible et la
            mobilité géographique entre les deux villes est forte. En pratique,
            on chasse souvent sur le bassin AMP entier (Aix + Marseille).
          </p>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            FAQ Aix-en-Provence
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les questions du marché aixois.
          </h2>
          <div className="mt-8 space-y-3">
            {FAQ_AIX.map((q) => (
              <details
                key={q.question}
                className="group rounded-2xl border border-slate-200 bg-white p-5"
              >
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-base font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                  <span>{q.question}</span>
                  <span className="mt-0.5 text-slate-400 transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <div className="mt-4 text-sm leading-relaxed text-slate-600">
                  {q.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Lien autres villes */}
      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-4xl px-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-pink-600" />
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-900">
                  Vous recrutez aussi sur Marseille ou la Côte d'Azur ?
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Pages dédiées :{' '}
                  <Link
                    to="/ia-paca/marseille"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Marseille
                  </Link>
                  {' · '}
                  <Link
                    to="/ia-paca/sophia-antipolis"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Sophia Antipolis
                  </Link>
                  {' · '}
                  <Link
                    to="/ia-paca/nice"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Nice
                  </Link>
                  {' · '}
                  <Link
                    to="/ia-paca"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Vue d'ensemble PACA
                  </Link>
                  .
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </div>
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Vous recrutez à Aix-en-Provence ?"
        subtitle="Notre cabinet couvre tout le bassin AMP (Aix + Marseille). Brief 30 min, court-list en 15-25 jours selon la rareté du profil et la zone."
      />
    </Layout>
  );
}

function TypologieCard({
  Icon,
  tag,
  title,
  body,
}: {
  Icon: typeof Building2;
  tag: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-violet-300 hover:shadow-md">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
        <Icon className="h-5 w-5" />
      </div>
      <span className="mt-3 inline-block rounded-full bg-pink-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-pink-700">
        {tag}
      </span>
      <h3 className="mt-2 text-base font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

const ECOSYSTEME_AIX = [
  {
    nom: 'Technopôle de l\'Arbois-Méditerranée',
    Icon: TreePine,
    description:
      "Premier technopôle français dédié à l'environnement. 250+ structures, dont une forte densité IA appliquée à l'énergie, l'eau, le climat. Lien étroit avec l'INRAE et le CEREGE.",
  },
  {
    nom: 'Pôle Pégase',
    Icon: Cpu,
    description:
      "Pôle de compétitivité aéronautique. Forte composante drones, systèmes embarqués IA, vision par ordinateur appliquée à l'aéronautique. Vivier de PME tech pointues.",
  },
  {
    nom: 'Aix-Marseille Université (AMU)',
    Icon: GraduationCap,
    description:
      "80 000 étudiants. Filières Data Science / IA en croissance (Polytech Marseille, IUT, MIAGE, Master IA). Excellent vivier de jeunes diplômés à Aix et Marseille.",
  },
  {
    nom: 'CEA Cadarache + ITER',
    Icon: FlaskConical,
    description:
      "Centre de recherche nucléaire majeur à 50 km au nord d'Aix. Le programme ITER (fusion) attire ingénieurs et chercheurs internationaux, génère des besoins IA (simulation, contrôle, optimisation).",
  },
  {
    nom: 'Aix Marseille French Tech (extension AMP)',
    Icon: Building2,
    description:
      "L'écosystème French Tech Aix-Marseille est piloté conjointement, avec des événements et programmes sur Aix (Hexagone, La Source). Bonne porte d'entrée pour le réseau startup local.",
  },
  {
    nom: 'Pôle SCS (zone aixoise)',
    Icon: Cpu,
    description:
      "Pôle de compétitivité Solutions Communicantes Sécurisées, ancré sur tout le bassin. Vertical IoT/IA/cyber, avec des projets collaboratifs entre grands groupes, PME et labos.",
  },
];

const FAQ_AIX = [
  {
    question: "Combien de profils IA disponibles sur le bassin Aix-Marseille-Provence ?",
    answer:
      "Notre estimation : ~2 500 à 3 200 profils IA / Data Science sur l'agglomération Aix-Marseille-Provence (AMP). Aix concentre ~25-30% de ce pool (vs ~50% Marseille et ~20-25% autour, Vitrolles, Rousset, Gardanne). En veille passive activable : 30-40% du pool selon poste.",
  },
  {
    question: "Pourquoi recruter à Aix plutôt qu'à Marseille pour mon ETI ?",
    answer:
      "Aix est mieux adaptée si : (1) votre ETI est implantée historiquement nord-Bouches-du-Rhône (Rousset, Pertuis, Gardanne), (2) vous cherchez des profils R&D / industriels (vs pure-play digital), (3) vous voulez un cadre de vie 'campus' (Arbois, Milles) plutôt qu'urbain. Marseille reste mieux pour les scale-ups digitales et l'attractivité junior.",
  },
  {
    question: "Vous travaillez avec les ETI industrielles autour d'Aix (Rousset, Pertuis) ?",
    answer:
      "Oui. Nous avons une bonne lecture des cas d'usage IA industrielle (maintenance prédictive, contrôle qualité computer vision, optimisation procédés, jumeau numérique). Ces ETI ont souvent besoin de profils mixtes : data science + compréhension métier industriel. Notre approche : sourcing + filtre fit culturel sur le terrain.",
  },
  {
    question: "Comment vous positionnez-vous face aux cabinets parisiens qui chassent à Aix ?",
    answer:
      "Les cabinets parisiens connaissent le marché parisien. Sur Aix, ils chassent à distance, sans réseau local, et alignent souvent des packages 'parisiens' qui font fuir les candidats (qui n'iront pas vivre à Paris). Notre avantage : on est sur place, on connaît les ETI, on calibre les packages selon le marché réel.",
  },
  {
    question: "Recrutez-vous aussi sur Vitrolles, Marignane, Gardanne ?",
    answer:
      "Oui. La métropole AMP (1.9 M hab.) est notre périmètre principal. Vitrolles (logistique, industrie), Marignane (aéronautique Airbus Helicopters), Gardanne (CMA Centre Microélectronique, énergie) sont des bassins industriels où l'IA appliquée se développe. Nous y avons un réseau de contacts mature.",
  },
];
